import dayjs from 'dayjs';
import React, {
  Component,
  Dispatch,
  FC,
  SetStateAction,
  useState,
} from 'react';
import { BlobLink } from '../../../components/BlobLink/BlobLink';
import { DataBox } from '../../../components/DataBox/DataBox';
import temperatureIcon from '../../../components/icons/temperatureIcon.svg';
import { regionSupportsTemperatureControl } from '../../../const/HubConfig';
import { DeliveryInfo } from '../../../fetch/orderFetch';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';
import { DropdownComponent } from '../../../ReusableComponents/Dropdown/DropdownComponent';
import { InputComponent } from '../../../ReusableComponents/Input/InputComponent';
import ModalComponent from '../../../ReusableComponents/Modal/ModalComponent';
import { TooltipComponent } from '../../../ReusableComponents/Tooltip/TooltipComponent';
import { settings } from '../../../settings';
import {
  BundleJson,
  CourierJson,
  InstructionType,
} from '../../../types/bundleTypes';
import {
  Address as OrderAddress,
  Delivery,
  DeliveryVerification,
  NotificationType,
  OrderClazz,
  OrderStatus,
  ParcelScan,
  Region,
  ResidenceType,
  ScanType,
  ServiceBrand,
  zoneNames,
} from '../../../types/orderTypes';
import { ordinal } from '../../../utils/strings';
import { packageSizeAsText } from '../../orders/OrdersUtil';
import Address from './Address';
import Fetch from '../../../utils/fetch';
import { Loader } from '../../../components/Loader/Loader';
import Alert from '../../../components/alerts/Alert';
import { DeliveryRating } from '../../delivery/DeliveryRating';
import OrderHistoryView from './OrderHistoryView';
import Table, { TableColumns } from 'components/table/Table';

import porterbuddyLogo from '../assets/porterbuddy_logo.png';
import instaboxLogo from '../assets/instabox_logo.png';
import { DeliveryImageModal } from './DeliveryImageModal';
import Swal from 'sweetalert2';

interface Props {
  order: OrderClazz;
  scans: ParcelScan[];
  bundle: BundleJson;
  region: Region;
  courier?: CourierJson;
  deliveryInfo?: DeliveryInfo;
  onAddressLocationChange: (longitude: number, latitude: number) => void;
  onSendTrackingLink: () => void;
  updateData: () => void;
  onSetContactlessDelivery: (delivery: Delivery, contactless: boolean) => void;
  onAddressChange?: (newAddress: OrderAddress) => void;
  setOrder: Dispatch<SetStateAction<OrderClazz>>;
}

const scansWithConfirm = new Set<ScanType>([
  ScanType.Partner,
  ScanType.HubSort,
  ScanType.Pickup,
]);

class OrderContent extends Component<Props> {
  displayOptionalBoolean = (
    attributeValue: boolean,
    overrideValue: boolean
  ) => {
    let displayValue = 'N/A';
    if (attributeValue === true) {
      displayValue = 'True';
    } else if (attributeValue === false) {
      displayValue = 'False';
    }
    if (attributeValue !== overrideValue) {
      return (
        <span title="Value overridden by other order in delivery">
          <s>{displayValue}</s> ({overrideValue ? 'True' : 'False'})
        </span>
      );
    }

    return <span>{displayValue}</span>;
  };

  displayOverridableNumber = (
    attributeValue?: number,
    overrideValue?: number
  ) => {
    if (attributeValue === overrideValue) {
      return <span>{attributeValue}</span>;
    }
    return (
      <span title="Value overridden by other order in delivery">
        <s>{attributeValue}</s> ({overrideValue})
      </span>
    );
  };

  displayScanConfirmed = (scan: ParcelScan) => {
    if (scansWithConfirm.has(scan.scanType)) {
      return scan.confirmed ? 'yes' : 'no';
    }
    return '-';
  };

  onDeleteImage = async (imageType: string) => {
    const { order, updateData } = this.props;
    try {
      const result = await Fetch.delete({
        url: `${settings.newApiUrl}/delivery/${order.deliveryId}/image/${imageType}`,
      });
      if (result.status !== 204) {
        await Swal.fire({
          title: 'Error deleting delivery images',
          text: `Returned status was ${result.status}`,
          icon: 'error',
        });
      } else {
        updateData();
      }
    } catch (e) {
      await Swal.fire({
        title: 'Error deleting delivery images',
        text: `Error was ${e.message}`,
        icon: 'error',
      });
    }
  };

  render(): JSX.Element {
    const {
      courier,
      order,
      bundle,
      scans,
      deliveryInfo,
      onSendTrackingLink,
      onSetContactlessDelivery,
      region,
    } = this.props;
    const deliveries =
      bundle?.instructions.filter(
        instr => instr.type === InstructionType.Delivery
      ) || [];
    const eta = deliveries.filter(
      instr =>
        instr.orderId === order.id && instr.type === InstructionType.Delivery
    )[0]?.eta;

    const deliveryPosition =
      deliveries.findIndex(instr => instr.orderId === order.id) + 1;

    deliveryInfo.orderIds = (deliveryInfo?.orderIds || []).filter(
      orderId => orderId !== order.id
    );
    const deliveryOrderCount = deliveryInfo ? deliveryInfo.orderIds.length : 0;
    const isConsolidatedDelivery = deliveryOrderCount > 0;
    const showMaxDeliveryIds = 4;
    const isTemperatureControlled =
      regionSupportsTemperatureControl(region) &&
      order.parcels.some(parcel => parcel.constraints?.temperature);
    const scanTypesToShowLocation: ScanType[] = [
      ScanType.Machine,
      ScanType.Hub,
      ScanType.HubSort,
      ScanType.Pickup,
    ];
    const parcelShipmentIdentifiers = order.parcels
      .filter(parcel => parcel.parcelShipmentIdentifier)
      .map(parcel => parcel.parcelShipmentIdentifier);

    const performedScanColumns: TableColumns[] = [
      {
        key: 'scanType',
        header: 'Type',
        align: 'center',
      },
      {
        key: 'scannedAt',
        header: 'Date',
        align: 'center',
        sortable: true,
        formatter: data => {
          return dayjs(data).format('DD.MM. HH:mm:ss');
        },
      },
      {
        key: 'location',
        header: 'Location',
        align: 'center',
        formatter: (data, rowData) => {
          if (data && scanTypesToShowLocation.includes(rowData.scanType)) {
            return data.pbScanLocation.replace('HUB_', '').replace('_', ' ');
          }
        },
      },
      {
        key: 'scannedBy',
        header: 'User',
        align: 'center',
      },
      {
        key: 'parcelId',
        header: 'Parcel Id',
        align: 'center',
        sortable: true,
        formatter: data => {
          return data || '-';
        },
      },
      {
        key: 'parcelShipmentIdentifier',
        header: 'Parcel Shipment Identifier',
        align: 'center',
        formatter: data => {
          return data || '-';
        },
      },
      {
        key: 'this.displayScanConfirmed(scan)',
        header: 'Confirmed',
        align: 'center',
        formatter: (_, rowData) => {
          return this.displayScanConfirmed(rowData);
        },
      },
      {
        key: 'shelfId',
        header: 'Shelf Id',
        align: 'center',
        formatter: data => {
          return data || '-';
        },
      },
      {
        key: 'scannedSize',
        header: 'Scanned Size',
        align: 'center',
        formatter: data => {
          return data || '-';
        },
      },
    ];

    return (
      <div>
        <div className="order__service-brand">
          <img
            src={
              order.brand === ServiceBrand.PORTERBUDDY
                ? porterbuddyLogo
                : instaboxLogo
            }
          />
        </div>
        <div className="order__information">
          <div className="information__box">
            <span className="box__field">
              <h5>Order ID:</h5>
              <h5>{order.id}</h5>
            </span>
            <span className="box__field">
              <h5>Bundle ID:</h5>
              {bundle ? (
                <h5
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <a href={`/bundles/${bundle.id}`}> {bundle.id} </a>
                  {bundle.shelfId && (
                    <span style={{ paddingLeft: '5px' }}>
                      ({bundle.shelfId})
                    </span>
                  )}
                </h5>
              ) : (
                <h5>N/A</h5>
              )}
            </span>
            <span className="box__field">
              <h5>Courier:</h5>
              <h5>{order.courierName || 'N/A'}</h5>
            </span>
            <span className="box__field">
              <h5>Courier phone:</h5>
              <h5>{courier?.phoneNumber || 'N/A'}</h5>
            </span>
            {isConsolidatedDelivery && (
              <TooltipComponent
                id="order-delivery-id-tooltip"
                title="Id used for consolidated deliveries"
                placement="bottom"
              >
                <span
                  className="box__field"
                  style={{ color: 'grey', cursor: 'help' }}
                >
                  <h5>Delivery ID:</h5>
                  <h5>{deliveryInfo.delivery.id}</h5>
                </span>
              </TooltipComponent>
            )}
            <div style={{ height: 25 }} />
            <span className="box__field">
              <h5>Delivery date:</h5>
              <h5>{dayjs(order.deliverFrom).format('DD.MM')}</h5>
            </span>
            <span className="box__field">
              <h5>Delivery window:</h5>
              <h5>
                {dayjs(order.deliverFrom).format('HH:mm')}–
                {dayjs(order.deliverUntil).format('HH:mm')}
              </h5>
            </span>
            {eta && eta.isValid() && (
              <span className="box__field">
                <h5>ETA:</h5>
                <h5>
                  {eta.format('HH:mm')}{' '}
                  <span style={{ fontSize: '1.2rem' }}>
                    ({ordinal(deliveryPosition)} of {deliveries.length})
                  </span>
                </h5>
              </span>
            )}
          </div>

          <div className="information__box">
            <span className="box__field">
              <h5>Product type: </h5>
              <h5>{order.product ? order.product.toUpperCase() : '-'}</h5>
            </span>
            <span className="box__field">
              <h5>Package size:</h5>
              <h5>{packageSizeAsText(order)}</h5>
            </span>
            <span className="box__field">
              <h5>Package count:</h5>
              <h5>{order.packagesNumber}</h5>
              {isTemperatureControlled && (
                <img
                  src={temperatureIcon}
                  style={{ height: 20, marginLeft: 8 }}
                  alt="Temperature control"
                />
              )}
            </span>
            {isConsolidatedDelivery && (
              <TooltipComponent
                id="order-delivery-orders-tooltip"
                title="Other orders in the same delivery"
                placement="bottom"
              >
                <span className="box__field" style={{ color: 'grey' }}>
                  <h5>Delivery orders:</h5>
                  <h5>
                    {deliveryInfo.orderIds
                      .filter(orderId => orderId !== order.id)
                      .filter((_, idx) => idx < showMaxDeliveryIds)
                      .map((orderId, idx) => (
                        <React.Fragment key={orderId}>
                          <a href={`/orders/${orderId}`}>{orderId}</a>
                          {idx < showMaxDeliveryIds - 1 &&
                            idx < deliveryInfo.orderIds.length - 1 &&
                            ', '}
                        </React.Fragment>
                      ))}
                    {deliveryOrderCount > showMaxDeliveryIds &&
                      `, ... (${deliveryOrderCount - showMaxDeliveryIds} more)`}
                  </h5>
                </span>
              </TooltipComponent>
            )}
            <div style={{ height: 25 }} />
            <span className="box__field">
              <h5>Region:</h5>
              <h5>{order.region || 'N/A'}</h5>
            </span>
            <span className="box__field">
              <h5>Zone:</h5>
              <h5>
                {order.geoCode || 'N/A'}
                {order.geoCode !== '' && (
                  <span style={{ fontSize: '1.2rem' }}>
                    &nbsp;&nbsp;({zoneNames[order.zone]})
                  </span>
                )}
              </h5>
            </span>
          </div>

          <div className="information__box">
            <span className="box__field">
              <h5>Pickup PIN:</h5>
              <h5>{order.pickupPin}</h5>
            </span>
            <span className="box__field hidden">
              <h5>Delivery PIN:</h5>
              <h5>{order.confirmationPin}</h5>
            </span>
            <span className="box__field">
              <div style={{ width: '100%' }}>
                <ButtonComponent
                  variant="outlined"
                  size="small"
                  color="primary"
                  disabled={!deliveryInfo.delivery.contactlessAllowed}
                  onClick={() =>
                    onSetContactlessDelivery(
                      deliveryInfo.delivery,
                      !deliveryInfo.delivery.confirmedContactless
                    )
                  }
                >
                  {deliveryInfo.delivery.confirmedContactless
                    ? 'Set personal delivery'
                    : 'Set contactless delivery'}
                </ButtonComponent>
              </div>
            </span>
            {isConsolidatedDelivery && (
              <span className="box__field box__field__spacer">
                <h5>&nbsp;</h5>
              </span>
            )}
            <div style={{ height: 25 }} />
            <span className="box__field">
              <h5>Inbound Region:</h5>
              <h5>{order.warehouseRegion}</h5>
            </span>
            {order.pickupDate && (
              <span className="box__field">
                <h5>Pickup date:</h5>
                <h5>{dayjs(order.pickupDate).format('DD.MM')}</h5>
              </span>
            )}
          </div>
        </div>

        <div style={{ height: 25 }} />

        <div className="order__information">
          <Address
            header="Sender"
            name={order.pickupName}
            email={order.pickupEmail}
            address={order.originAddress.fullAddressString}
            phone={order.pickupPhone}
            longitude={order.originAddress.location.longitude}
            latitude={order.originAddress.location.latitude}
            validity={order.originAddress.addressValidity}
          />

          <Address
            header="Recipient"
            name={order.recipientName}
            email={order.recipientEmail}
            address={order.recipientAddress}
            phone={order.recipientPhone}
            longitude={order.recipientLongitude}
            latitude={order.recipientLatitude}
            validity={order.recipientAddressValidity}
            onAddressLocationChange={this.props.onAddressLocationChange}
            onAddressChange={this.props.onAddressChange}
          />
          <div className="order__information__buttons">
            <div style={{ width: '100%' }}>
              <ButtonComponent
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => onSendTrackingLink()}
              >
                Send tracking link
              </ButtonComponent>
            </div>
          </div>
        </div>

        {order.orderReference && (
          <div style={{ paddingTop: 10, fontSize: '1.2rem' }}>
            <span>
              <span>Order reference: </span>
              <span>{order.orderReference}</span>
            </span>
          </div>
        )}
        {parcelShipmentIdentifiers.length > 0 && (
          <div style={{ paddingTop: 10, fontSize: '1.2rem' }}>
            <span>
              <span>Parcel Shipment Identifiers: </span>
              <span>{parcelShipmentIdentifiers.join(', ')}</span>
            </span>
          </div>
        )}

        <div style={{ height: 15 }} />
        <OrderHistoryView orderId={order.id} />
        <div className="grid has-gutter">
          {order.status === OrderStatus.Delivered && (
            <div className="grid__col u-x1of1 u-x1of1-medium order__feedback-section">
              <DeliveryRating
                deliveredAt={dayjs(new Date(order.deliveredAt))}
                rating={deliveryInfo.delivery.deliveryRating}
                feedback={deliveryInfo.delivery.feedback}
                feedbackFlags={deliveryInfo.delivery.feedbackFlags}
                deliveryToken={deliveryInfo.delivery.token}
                photoRating={deliveryInfo.delivery.photoRating}
                refetchData={() => this.props.updateData()}
              />
            </div>
          )}
          <div className="grid__col u-x1of1 u-x1of1-medium">
            <DataBox title="Notes">
              <dl className="definitionlist">
                <dt>For courier:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {order.noteForCourier
                    ? order.noteForCourier.split('\n').map((item, i) => (
                        <p style={{ wordBreak: 'break-all' }} key={i}>
                          {item}
                        </p>
                      ))
                    : '-'}
                </dd>
                <dt>Technical:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {order.technicalNotes
                    ? order.technicalNotes
                        .split('\n')
                        .map((item, i) => (
                          <TechnicalNoteLine line={item} key={i} />
                        ))
                    : '-'}
                </dd>
                <dt>Floor:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions.floor
                    ? deliveryInfo.delivery.userInstructions.floor
                    : '-'}
                </dd>
                <dt>DoorbellName:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions.doorbellName
                    ? deliveryInfo.delivery.userInstructions.doorbellName
                    : '-'}
                </dd>
                <dt>NotificationType:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions.notificationType
                    ? deliveryInfo.delivery.userInstructions.notificationType
                    : '-'}
                </dd>
                <dt>ResidenceType:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions.residenceType
                    ? deliveryInfo.delivery.userInstructions.residenceType
                    : '-'}
                </dd>
                <dt>Note:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions.note
                    ? deliveryInfo.delivery.userInstructions.note
                    : '-'}
                </dd>
                <dt>Package place information:</dt>
                <dd style={{ fontFamily: 'Courier New' }}>
                  {deliveryInfo.delivery.userInstructions
                    .packagePlaceInformation
                    ? deliveryInfo.delivery.userInstructions
                        .packagePlaceInformation
                    : '-'}
                </dd>
              </dl>
              <dl className="definitionlist">
                <dt>
                  {deliveryInfo.delivery.confirmedContactless
                    ? 'Contactless delivery'
                    : 'Personal delivery'}
                </dt>
              </dl>
              <EditUserInstructionsModal
                delivery={deliveryInfo.delivery}
                refetchData={() => this.props.updateData()}
              />
              <AddTechnicalNoteModal
                order={order}
                refetchData={() => this.props.updateData()}
              />
            </DataBox>
          </div>
          <div className="grid__col u-x1of1 u-x1of2-medium">
            <DataBox title="Required validations">
              <dl className="definitionlist">
                <dt>Require Personal delivery:</dt>
                <dd>
                  {this.displayOptionalBoolean(
                    order.verifications.deliveryVerification ===
                      DeliveryVerification.REQUIRE_PIN,
                    deliveryInfo.delivery.verifications.deliveryVerification ===
                      DeliveryVerification.REQUIRE_PIN
                  )}
                </dd>
                <dt>Only to recipient:</dt>
                <dd>
                  {this.displayOptionalBoolean(
                    order.verifications.deliveryVerification ===
                      DeliveryVerification.ONLY_TO_RECIPIENT,
                    deliveryInfo.delivery.verifications.deliveryVerification ===
                      DeliveryVerification.ONLY_TO_RECIPIENT
                  )}
                </dd>
                <dt>Minimum Age Check:</dt>
                <dd>
                  {this.displayOverridableNumber(
                    order.verifications.minimumAgeCheck,
                    deliveryInfo.delivery.verifications.minimumAgeCheck
                  )}
                </dd>
                <dt>ID check:</dt>
                <dd>
                  {this.displayOptionalBoolean(
                    order.verifications.deliveryVerification ===
                      DeliveryVerification.REQUIRE_ID,
                    deliveryInfo.delivery.verifications.deliveryVerification ===
                      DeliveryVerification.REQUIRE_ID
                  )}
                </dd>
                <dt>Signature:</dt>
                <dd>
                  {this.displayOptionalBoolean(
                    order.verifications.requireSignature,
                    deliveryInfo.delivery.verifications.requireSignature
                  )}
                </dd>
                <dt>Confirm Customer Receipt:</dt>
                <dd>
                  {this.displayOptionalBoolean(
                    order.verifications.confirmCustomerReceipt,
                    deliveryInfo.delivery.verifications.confirmCustomerReceipt
                  )}
                </dd>
              </dl>
            </DataBox>
          </div>
          <div className="grid__col u-x1of1 u-x1of2-medium">
            <DataBox title="Performed validations">
              <dl className="definitionlist">
                <dt>ID check performed:</dt>
                <dd>
                  {deliveryInfo.delivery.performedVerifications.idCheckName
                    ? `Yes (${deliveryInfo.delivery.performedVerifications.idCheckName})`
                    : 'No'}
                </dd>
                <dt>Minimum Age checked:</dt>
                <dd>
                  {deliveryInfo.delivery.performedVerifications.ageVerified}
                </dd>
                <dt>Signature image:</dt>
                <dd>
                  {deliveryInfo.delivery.performedVerifications
                    .signatureVerified ? (
                    <DeliveryImageModal
                      title={'Open'}
                      url={`${settings.newApiUrl}/delivery/${order.deliveryId}/image/signature`}
                      showDeleteButton={false}
                    />
                  ) : (
                    <span>None</span>
                  )}
                </dd>
                <dt>Confirmation photo:</dt>
                <dd>
                  {deliveryInfo.delivery.performedVerifications
                    .confirmationPhoto ? (
                    <DeliveryImageModal
                      title="Delivery Confirmation Photo"
                      url={`${settings.newApiUrl}/delivery/${order.deliveryId}/image/delivery_confirmation`}
                      showDeleteButton={true}
                      onDeleteImage={() =>
                        this.onDeleteImage('delivery_confirmation')
                      }
                    />
                  ) : (
                    <span>None</span>
                  )}
                </dd>
                <dt>Delivery location verified:</dt>
                <dd>
                  {deliveryInfo.delivery.deliveryLocationVerified
                    ? 'True'
                    : 'False'}
                </dd>
                <dt>Reception confirmed:</dt>
                <dd>
                  {deliveryInfo.delivery.performedVerifications
                    .customerConfirmedReceptionAt &&
                    dayjs(
                      deliveryInfo.delivery.performedVerifications
                        .customerConfirmedReceptionAt
                    ).format('DD.MM. HH:mm:ss')}
                </dd>
              </dl>
            </DataBox>
          </div>
          <div className="grid__col u-x1of1">
            <DataBox title="Performed Scans">
              <Table
                borderless
                className="scan-report-table"
                idProperty="performedScan"
                defaultSort={{ sortKey: 'scannedAt', sortOrdering: 'asc' }}
                loading={false}
                data={scans}
                columns={performedScanColumns}
              />
            </DataBox>
          </div>
          <a
            href={`${settings.newApiUrl}/order/${order.id}/label/${order.token}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Print label
          </a>
        </div>
      </div>
    );
  }
}

const TechnicalNoteLine: FC<{ line: string }> = ({ line }) => {
  if (line.includes(': Customer has changed')) {
    const [timestamp, message] = line.split(': ');
    return (
      <p>
        {timestamp}: <span style={{ color: 'red' }}>{message}</span>
      </p>
    );
  }
  return <p style={{ wordBreak: 'break-all' }}>{line}</p>;
};

const EditUserInstructionsModal: FC<{
  delivery: Delivery;
  refetchData: () => void;
}> = ({ delivery, refetchData }) => {
  const [showUserInstructionModal, setShowUserInstructionModal] =
    useState(false);
  const [floor, setFloor] = useState(delivery.userInstructions.floor);
  const [doorbellName, setDoorbellName] = useState(
    delivery.userInstructions.doorbellName
  );
  const [notificationType, setNotificationType] = useState(
    delivery.userInstructions.notificationType
  );
  const [residenceType, setResidenceType] = useState(
    delivery.userInstructions.residenceType
  );
  const [note, setNote] = useState(delivery.userInstructions.note);
  const [packagePlaceInformation, setPackagePlaceInformation] = useState(
    delivery.userInstructions.packagePlaceInformation
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSaveUserInstructionsClick = async (): Promise<void> => {
    setLoading(true);
    const response = await Fetch.put({
      url: `${settings.newApiUrl}/delivery/${delivery.token}/user-instructions`,
      body: {
        floor,
        doorbellName,
        notificationType,
        residenceType,
        note,
        packagePlaceInformation,
      },
    });

    if (response.ok) {
      refetchData();
      setShowUserInstructionModal(false);
    } else {
      setError('Something unexpected happened');
    }
    setLoading(false);
  };

  return (
    <>
      <ButtonComponent
        variant="outlined"
        color="primary"
        onClick={() => setShowUserInstructionModal(true)}
        style={{ marginTop: '10px' }}
      >
        Edit user instructions
      </ButtonComponent>
      <ModalComponent
        open={showUserInstructionModal}
        setOpen={setShowUserInstructionModal}
      >
        <h1>Edit user instruction</h1>
        <InputComponent
          id="floor"
          name="floor"
          label="Floor"
          variant="outlined"
          value={floor}
          fullWidth
          type="number"
          onChange={event => setFloor(parseInt(event.target.value, 10))}
        />
        <InputComponent
          id="doorbellName"
          name="doorbellName"
          label="Doorbell name"
          variant="outlined"
          value={doorbellName}
          fullWidth
          onChange={event => setDoorbellName(event.target.value)}
        />
        <div style={{ height: 16 }} />
        <DropdownComponent
          id="notificationType"
          labelId="notificationType"
          label="Notification type"
          value={notificationType}
          onChange={event =>
            setNotificationType(event.target.value as NotificationType)
          }
          itemsToSelect={[
            {
              value: null,
              name: '-',
            },
            {
              value: 'DOORBELL',
              name: 'Doorbell',
            },
            {
              value: 'KNOCK',
              name: 'Knock',
            },
            {
              value: 'CALL',
              name: 'Call',
            },
            {
              value: 'SMS',
              name: 'SMS',
            },
            {
              value: 'NO_NOTIFICATION',
              name: 'No notification',
            },
          ]}
        />
        <div style={{ height: 16 }} />
        <DropdownComponent
          id="residenceType"
          labelId="residenceType"
          label="Residence type"
          value={residenceType}
          onChange={event =>
            setResidenceType(event.target.value as ResidenceType)
          }
          itemsToSelect={[
            {
              value: null,
              name: '-',
            },
            {
              value: 'HOUSE',
              name: 'House',
            },
            {
              value: 'APARTMENT',
              name: 'Apartment',
            },
          ]}
        />
        <div style={{ height: 16 }} />
        <InputComponent
          id="note"
          name="note"
          label="Note"
          variant="outlined"
          value={note}
          fullWidth
          onChange={event => setNote(event.target.value)}
        />
        <InputComponent
          id="packagePlaceInformation"
          name="packagePlaceInformation"
          label="Package place information"
          variant="outlined"
          value={packagePlaceInformation}
          fullWidth
          onChange={event => setPackagePlaceInformation(event.target.value)}
        />
        <div style={{ height: 24 }} />
        {error && (
          <Alert type="danger" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}
        <ButtonComponent
          variant="outlined"
          color="primary"
          onClick={handleSaveUserInstructionsClick}
        >
          Save {loading && <Loader />}
        </ButtonComponent>
      </ModalComponent>
    </>
  );
};

const AddTechnicalNoteModal: FC<{
  order: OrderClazz;
  refetchData: () => void;
}> = ({ order, refetchData }) => {
  const [showTechnicalNoteModal, setShowTechnicalNoteModal] = useState(false);
  const [technicalNote, setTechnicalNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddTechnicalNoteClick = async () => {
    setLoading(true);
    const response = await Fetch.put({
      url: `${settings.newApiUrl}/order/${order.id}/technical-note`,
      body: {
        note: technicalNote,
      },
    });
    if (response.ok) {
      refetchData();
      setTechnicalNote('');
      setShowTechnicalNoteModal(false);
    } else {
      setError('Something unexpected happened');
    }

    setLoading(false);
  };

  return (
    <>
      <ButtonComponent
        variant="outlined"
        color="primary"
        style={{ marginTop: '10px' }}
        onClick={() => setShowTechnicalNoteModal(true)}
      >
        Add technical note
      </ButtonComponent>
      <ModalComponent
        open={showTechnicalNoteModal}
        setOpen={setShowTechnicalNoteModal}
      >
        <h1>Add technical note</h1>
        <InputComponent
          id="technicalNote"
          name="technicalNote"
          label="Add technical note"
          variant="outlined"
          textAreaMode={true}
          value={technicalNote}
          fullWidth
          onChange={event => setTechnicalNote(event.target.value)}
        />
        {error && (
          <Alert type="danger" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}
        <ButtonComponent
          variant="outlined"
          color="primary"
          onClick={() => handleAddTechnicalNoteClick()}
        >
          Save {loading && <Loader />}
        </ButtonComponent>
      </ModalComponent>
    </>
  );
};

export default OrderContent;
